import { CommonModule } from '@angular/common';

import { HttpErrorResponse } from '@angular/common/http';

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  Renderer2,
  inject,
  signal,
} from '@angular/core';

import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Router, RouterLink } from '@angular/router';
import { isFreeEmail } from 'free-email-domains-list';
import { Subject, catchError, map, take, takeUntil, throwError } from 'rxjs';

import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Store } from '@ngrx/store';
import { OktaUserService } from 'src/app/manage-users/services/okta-user/okta-user.service';
import { SharedModule } from 'src/app/shared/shared.module';
import * as fromStore from '../../store';
import { OktaMigrationTimerService } from './okta-migration-timer.service';

export interface StartOktaTransitionRequest {
  email: string;
}

export interface StartOktaTransitionResponse {
  success: boolean;
  email: string;
}

@Component({
  selector: 'app-okta-migration',

  standalone: true,

  templateUrl: './okta-migration.component.html',

  changeDetection: ChangeDetectionStrategy.OnPush,

  imports: [
    CommonModule,
    ReactiveFormsModule,
    SharedModule,
    RouterLink,
    MatProgressSpinnerModule,
    MatSnackBarModule,
  ],
})
export class OktaMigrationComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private readonly oktaUserService = inject(OktaUserService);

  private readonly snackBar = inject(MatSnackBar);

  private store = inject(Store<fromStore.State>);

  private readonly renderer = inject(Renderer2);

  private readonly router = inject(Router);

  readonly migrationTimer = inject(OktaMigrationTimerService);

  private destroy$ = new Subject<void>();

  private popStateListener: (() => void) | null = null;

  loggedInUser = signal(null);

  isLoading = signal(false);

  errorMessage = signal<string | null>(null);

  submitted = signal(false);

  submitting = signal(false);

  activationEmail = signal('');

  currentStep = signal<'form' | 'confirmation'>('form');

  private readonly STORAGE_KEY_STEP = 'okta-migration-step';

  private readonly STORAGE_KEY_EMAIL = 'okta-migration-email';

  messages = signal({
    emailInput: '',
  });

  private readonly allowedCorporateDomains = [
    'comcast.com',
    'cable.comcast.com',
    'xfinity.com',
    'xfinitymail.com',
    'nbcuni.com',
    'nbc.com',
    'nbcuniversal.com',
  ];

  private isDomainOrSubdomain(domain: string, base: string): boolean {
    return domain === base || domain.endsWith(`.${base}`);
  }

  private nonPublicEmailValidator = (
    control: AbstractControl,
  ): ValidationErrors | null => {
    const email = String(control.value ?? '')
      .trim()
      .toLowerCase();
    const at = email.lastIndexOf('@');
    if (at < 0 || at === email.length - 1) return null;

    const domain = email.slice(at + 1);

    if (
      this.allowedCorporateDomains.some((d) =>
        this.isDomainOrSubdomain(domain, d),
      )
    ) {
      return null;
    }

    return isFreeEmail(email) ? { publicEmailDomain: true } : null;
  };

  formGroup: FormGroup<{
    email: FormControl<string>;
  }> = new FormGroup({
    email: new FormControl('', {
      nonNullable: true,

      validators: [
        Validators.required,
        Validators.email,
        Validators.maxLength(254),
        // this.nonPublicEmailValidator,
      ],
    }),
  });

  ngOnInit(): void {
    // Restore state after page refresh
    const savedStep = sessionStorage.getItem(this.STORAGE_KEY_STEP);
    const savedEmail = sessionStorage.getItem(this.STORAGE_KEY_EMAIL);
    if (savedStep === 'confirmation' && savedEmail) {
      this.currentStep.set('confirmation');
      this.activationEmail.set(savedEmail);
      this.migrationTimer.start();
      this.setupPopStateGuard();
    }

    this.store
      .select(fromStore.getCurrentUser)
      .pipe(
        map((user) => {
          this.loggedInUser.set(user);
          if (user?.Email) {
            this.formGroup.controls.email.setValue(user.Email);
          }
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  ngAfterViewInit(): void {
    this.focusElement('#transitionEmailId');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.popStateListener !== null) {
      window.removeEventListener('popstate', this.popStateListener);
    }
    this.clearPersistedState();
  }

  submit(): void {
    this.submitted.set(true);
    this.messages.update((m) => ({ ...m, emailInput: '' }));
    this.errorMessage.set(null);

    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();

      const emailControl = this.formGroup.controls.email;
      const errors = emailControl.errors as Record<string, boolean> | null;

      if (errors?.required) {
        this.messages.update((m) => ({
          ...m,
          emailInput: 'Please enter your email address.',
        }));
      } else if (errors?.email) {
        this.messages.update((m) => ({
          ...m,
          emailInput: 'Please enter a valid email address.',
        }));
      } else if (errors?.maxlength) {
        this.messages.update((m) => ({
          ...m,
          emailInput: 'Email address must be 254 characters or fewer.',
        }));
      } else if (errors?.publicEmailDomain) {
        this.messages.update((m) => ({
          ...m,
          emailInput: 'Please use a company email address (no public domains).',
        }));
      }
      return;
    }

    const email = this.formGroup.controls.email.value.trim();

    this.isLoading.set(true);
    this.oktaUserService
      .startOktaMigration(email)
      .pipe(
        take(1),
        catchError(
          (
            error:
              | HttpErrorResponse
              | { code: string; message: string; status: string },
          ) => {
            this.isLoading.set(false);
            this.errorMessage.set(
              'code' in error && error.code === 'DUPLICATE_VALUE'
                ? 'An account with this email already exists. Please use a different email address.'
                : "We couldn't start your transition right now. Please try again later.",
            );
            return throwError(() => error);
          },
        ),
      )
      .subscribe(() => {
        this.isLoading.set(false);
        this.activationEmail.set(email);
        this.currentStep.set('confirmation');
        this.migrationTimer.start();

        // Persist state for page refresh
        sessionStorage.setItem(this.STORAGE_KEY_STEP, 'confirmation');
        sessionStorage.setItem(this.STORAGE_KEY_EMAIL, email);

        // Prevent browser back from returning to the form step
        this.setupPopStateGuard();
      });
  }

  resendEmail(): void {
    if (!this.activationEmail()) {
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set(null);

    this.oktaUserService
      .resendActivationEmail(this.activationEmail())
      .pipe(
        take(1),
        catchError((error: HttpErrorResponse) => {
          this.isLoading.set(false);
          this.errorMessage.set(
            "We couldn't resend the activation email right now. Please try again later.",
          );
          return throwError(() => error);
        }),
      )
      .subscribe(() => {
        this.isLoading.set(false);
        this.snackBar.open('Activation email resent successfully', 'Close', {
          duration: 7000,
          panelClass: ['success-snackbar'],
        });
      });
  }

  signOut(): void {
    this.clearPersistedState();
    this.migrationTimer.signOutNow();
  }

  goToDashboard(): void {
    this.clearPersistedState();
    this.router.navigate(['/dashboard']);
  }

  private focusElement(selector: string): void {
    setTimeout(() => {
      try {
        this.renderer.selectRootElement(selector).focus();
      } catch {
        // no-op
      }
    });
  }

  private setupPopStateGuard(): void {
    window.history.pushState({ step: 'confirmation' }, '');
    this.popStateListener = () => {
      if (this.currentStep() === 'confirmation') {
        window.history.pushState({ step: 'confirmation' }, '');
      }
    };
    window.addEventListener('popstate', this.popStateListener);
  }

  private clearPersistedState(): void {
    sessionStorage.removeItem(this.STORAGE_KEY_STEP);
    sessionStorage.removeItem(this.STORAGE_KEY_EMAIL);
  }
}
